import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  HiOutlineTicket,
  HiOutlineClock,
  HiOutlineCalendarDays,
  HiOutlineUsers,
  HiOutlineArchiveBox,
  HiOutlineBanknotes,
  HiOutlineChartBar,
  HiOutlineCurrencyDollar,
  HiOutlineCalendar,
  HiOutlineTrophy,
  HiOutlineArrowRightOnRectangle,
  HiOutlineBars3,
  HiOutlineXMark,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineDocumentCheck,
  HiOutlineExclamationTriangle,
  HiOutlineChevronDown,
  HiOutlineCpuChip,
} from "react-icons/hi2";

const LOGO_URL = "/logo_ar_sombra.png";

const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (!user || location.pathname === "/login") {
    return null;
  }

  const sidebarW = collapsed ? "w-[72px]" : "w-[260px]";

  const navSections = [
    {
      title: "Reservas",
      items: [
        { label: "Buscar Ticket", path: "/", icon: HiOutlineTicket },
        { label: "Actividad", path: "/actividad", icon: HiOutlineClock },
        { label: "Apartar Técnico", path: "/apartar_tecnico", icon: HiOutlineCalendarDays },
        ...(isAdmin
          ? [
              { label: "Agendar", path: "/pendientes", icon: HiOutlineDocumentCheck },
              { label: "Actividades Pendientes", path: "/actividades_pendientes", icon: HiOutlineExclamationTriangle },
              { label: "Archivados", path: "/archivados", icon: HiOutlineArchiveBox },
              { label: "Overview Checador", path: "/overview_checadas", icon: HiOutlineAdjustmentsHorizontal },
            ]
          : []),
      ],
    },
    ...(isAdmin
      ? [
          {
            title: "Gestión",
            items: [
              { label: "Técnicos", path: "/tecnicos", icon: HiOutlineUsers },
              { label: "Competencias", path: "/competencias", icon: HiOutlineCpuChip },
              { label: "Bonos", path: "/bonos", icon: HiOutlineBanknotes },
              { label: "Reporte Bono", path: "/bonos_search", icon: HiOutlineTrophy },
              { label: "Gestor Gastos", path: "/gestor_gastos", icon: HiOutlineCurrencyDollar },
            ],
          },
        ]
      : []),
    {
      title: "Análisis",
      items: [
        { label: "Diagrama", path: "/diagrama", icon: HiOutlineChartBar },
        ...(isAdmin
          ? [{ label: "Panel de Control", path: "/panel_control_informacion", icon: HiOutlineCalendar }]
          : []),
      ],
    },
  ];

  // Current page label for topbar
  const currentLabel = navSections
    .flatMap((s) => s.items)
    .find((i) => isActive(i.path))?.label || "Dashboard";

  return (
    <>
      {/* ───── Mobile overlay ───── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/45 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ───── Sidebar ───── */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-dvh flex flex-col
          bg-sidebar border-r border-white/[0.06]
          transition-all duration-300 ease-[cubic-bezier(.4,0,.2,1)]
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
          ${sidebarW}
        `}
      >
        {/* Logo area */}
        <div className={`flex items-center shrink-0 h-16 border-b border-white/[0.06] ${collapsed ? "justify-center px-0" : "px-5 justify-between"}`}>
          <Link
            to="/"
            className="flex items-center gap-2.5 group"
            onClick={() => setSidebarOpen(false)}
          >
            {collapsed ? (
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white text-xs font-bold tracking-wide group-hover:bg-white/[0.15] transition-colors">
                AR
              </div>
            ) : (
              <img src={LOGO_URL} alt="Logo" className="h-12 object-contain" />
            )}
          </Link>
          <button
            className="lg:hidden text-zinc-400 hover:text-white transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className={`flex-1 overflow-y-auto overflow-x-hidden py-5 ${collapsed ? "px-2" : "px-3"} space-y-7 sidebar-scroll`}>
          {navSections.map((section) => (
            <div key={section.title}>
              {!collapsed && (
                <p className="px-3 mb-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                  {section.title}
                </p>
              )}
              {collapsed && <div className="h-px bg-white/[0.06] mx-1 mb-2" />}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setSidebarOpen(false);
                      }}
                      title={collapsed ? item.label : undefined}
                      className={`
                        group relative flex items-center w-full rounded-lg transition-all duration-150
                        ${collapsed ? "justify-center h-10 w-10 mx-auto" : "gap-3 px-3 py-2"}
                        ${active
                          ? "bg-sidebar-hover text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
                          : "text-zinc-300 hover:bg-sidebar-hover hover:text-white"
                        }
                      `}
                    >
                      {active && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-brand-primary" />
                      )}
                      <Icon className={`flex-shrink-0 ${collapsed ? "w-5 h-5" : "w-[18px] h-[18px]"}`} />
                      {!collapsed && (
                        <span className="text-[13px] font-medium truncate">{item.label}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Collapse toggle — desktop only */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center h-10 mx-3 mb-2 rounded-lg text-zinc-400 hover:text-white hover:bg-sidebar-hover transition-colors"
          title={collapsed ? "Expandir" : "Colapsar"}
        >
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${collapsed ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
      </aside>

      {/* ───── Topbar (desktop) ───── */}
      <header
        className={`
          hidden lg:flex fixed top-0 right-0 z-30 h-16 items-center justify-between
          bg-white border-b border-surface-border
          transition-all duration-300
          ${collapsed ? "left-[72px]" : "left-[260px]"}
          px-8
        `}
      >
        {/* Left: breadcrumb */}
        <div className="flex items-center gap-3">
          <span className="text-[13px] text-texttone-secondary font-medium">Dashboard</span>
          <span className="text-surface-border">/</span>
          <span className="text-[13px] text-texttone-primary font-semibold">{currentLabel}</span>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-full hover:bg-surface-app transition-colors"
          >
            {user.photoURL ? (
              <img src={user.photoURL} alt="" className="w-8 h-8 rounded-full ring-1 ring-slate-200" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold">
                {user.displayName?.[0] || "U"}
              </div>
            )}
            <span className="text-sm font-medium text-slate-700 hidden xl:inline">
              {user.displayName?.split(" ")[0]}
            </span>
            <HiOutlineChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`} />
          </button>

          {/* Dropdown */}
          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl border border-surface-border shadow-lg py-1.5 z-50">
              <div className="px-4 py-3 border-b border-surface-border">
                <p className="text-sm font-semibold text-slate-900 truncate">{user.displayName}</p>
                <p className="text-xs text-texttone-secondary truncate">{user.email}</p>
              </div>
              <button
                onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-brand-primary hover:bg-red-50 transition-colors"
              >
                <HiOutlineArrowRightOnRectangle className="w-4 h-4" />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ───── Topbar (mobile) ───── */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-white border-b border-surface-border flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <button className="btn-icon" onClick={() => setSidebarOpen(true)}>
            <HiOutlineBars3 className="w-6 h-6" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-900 tracking-tight">AR Tecnología</span>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {user.photoURL ? (
            <img src={user.photoURL} alt="" className="w-8 h-8 rounded-full ring-1 ring-slate-200" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold">
              {user.displayName?.[0] || "U"}
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
